import { useState } from "react";

import { Card } from "@mui/material";
import { CurveType, LineChart } from "@mui/x-charts";

import { CampaignAnalyticsService } from "@analytics/index";

import useChartRange, { RangeType } from "@hooks/useChartRange";

import { EmailType } from "@interfaces/campaign.type";

import CardHeader from "@pages/Campaigns/components/EngagementAndLeadCards/CardHeader";

import theme from "@theme/index";

const chartConfig = {
  linkClicked: {
    area: true,
    color: theme.palette.info.main,
    curve: "linear" as CurveType,
    label: "Link Clicked",
  },
  opened: {
    area: true,
    color: theme.palette.warning.light,
    curve: "linear" as CurveType,
    label: "Opened",
  },
  replyReceived: {
    area: true,
    color: theme.palette.primary.main,
    curve: "linear" as CurveType,
    label: "Reply Received",
  },
};

interface ICampaignChartProperties {
  clickedEmails: number;
  emails: EmailType[];
  openedEmails: number;
  repliedEmails: number;
}

export default function CampaignChart({
  clickedEmails,
  emails,
  openedEmails,
  repliedEmails,
}: ICampaignChartProperties) {
  const [activeRange, setActiveRange] = useState<RangeType>("24h");

  const { series, xAxis } = useChartRange(emails, activeRange);

  const handleSetActiveRange = (range: RangeType) => {
    setActiveRange(range);
  };

  if (!clickedEmails && !openedEmails && !repliedEmails) {
    return;
  }

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.grey[50],

        color: theme.palette.primary.contrastText,
        display: "flex",
        flexDirection: "column",
        height: "470px",
        padding: 3,
      }}
    >
      <CardHeader
        activeRange={activeRange}
        hasClicked={!!clickedEmails}
        hasOpened={!!openedEmails}
        hasResponded={!!repliedEmails}
        setActiveRange={handleSetActiveRange}
        shouldShowDropdown
        title="Engagement Over Time"
      />
      <LineChart
        grid={{
          horizontal: true,
          vertical: true,
        }}
        series={Object.entries(series).map(([key, value]) => ({
          data: value,
          ...chartConfig[key as keyof typeof chartConfig],
        }))}
        sx={{
          ".MuiAreaElement-root": {
            opacity: 0.6,
          },
          ".MuiChartsAxis-line": {
            stroke: theme.palette.grey[300],
          },
          ".MuiChartsGrid-line": {
            stroke: theme.palette.grey[300],
          },
        }}
        xAxis={[{ ...xAxis }]}
      />
    </Card>
  );
}
